import React from 'react'
import { InputNumber, Space, Typography } from 'antd'
import type { LineItem } from '../../types/ffe'
import { formatCurrency } from '../../utils/currency'
import { useFFEStore } from '../../store/ffeStore'
import { MaterialsButton } from './MaterialsButton'
import { DeliveryButton } from './DeliveryButton'

const { Text } = Typography

interface Props {
  item: LineItem
  onUpdate: (updates: Partial<LineItem>) => void
}

export const getNewMaterialQuantity = (
  materialQuantity: number,
  oldItemQuantity: number,
  newItemQuantity: number
) => {
  if (oldItemQuantity === 0 || materialQuantity === 0) return materialQuantity
  // has decimal material quantity, keep decimal
  if (materialQuantity % 1 !== 0) {
    return (materialQuantity / oldItemQuantity) * newItemQuantity
  }

  // for whole units don't fuck it up.
  return Math.ceil((materialQuantity / oldItemQuantity) * newItemQuantity)
}

export const LineItemPricing: React.FC<Props> = ({ item, onUpdate }) => {
  // const isUpdatingRef = useRef(false)
  const materials = useFFEStore((state) => state.document.materials)

  // Calculate margin percentage
  // const margin =
  //   (((item.sellPrice || 0) - (item.costPrice || 0)) / (item.costPrice || 1)) *
  //   100

  // Calculate materials cost
  const materialsCost =
    item.materials?.reduce((total, mat) => {
      const material = materials.find((m) => m.id === mat.materialId)
      if (!material || material.materialType !== 'finish') return total
      return total + material.sellPricePerUnit * mat.quantity * item.quantity
    }, 0) || 0

  const accessoriesCost =
    item.materials?.reduce((total, mat) => {
      const material = materials.find((m) => m.id === mat.materialId)
      if (!material || material.materialType !== 'accessory') return total
      return total + material.sellPricePerUnit * mat.quantity * item.quantity
    }, 0) || 0

  // Calculate total including materials
  const itemTotal = (item.sellPrice || 0) * (item.quantity || 0)
  const total = itemTotal + materialsCost + accessoriesCost

  // Handle margin change
  const handleMarginChange = (marginPercent: number | null) => {
    // if (isUpdatingRef.current || marginPercent === null) return
    if (marginPercent === null) return

    // isUpdatingRef.current = true
    const costPrice = item.costPrice || 0
    const newSellPrice = costPrice * (1 + marginPercent / 100)
    onUpdate({
      margin: marginPercent,
      sellPrice: Number(newSellPrice.toFixed(2))
    })
    // isUpdatingRef.current = false
  }

  const updateCostPrice = (value: number | null) => {
    if (value === null) return
    // if (value) {
    onUpdate({
      costPrice: value || 0,
      sellPrice: value + (value * item.margin) / 100
    })
    // } else {
    //   onUpdate({ costPrice: value || 0 })
    // }
  }

  const updateSellPrice = (value: number | null) => {
    if (value === null) return
    onUpdate({
      margin: ((value - item.costPrice) / item.costPrice) * 100,
      sellPrice: value || 0
    })
  }

  const changeQuantity = (value: number | null) => {
    const newValue = value || 0
    onUpdate({
      quantity: newValue
    })
  }

  const isDeliveryItem = item.systemCategory === 'DELIVERY'

  return (
    <div className="w-full justify-between flex flex-col min-[1430px]:flex-row items-stretch ps-[0px] md:ps-[130px]">
      <div className='flex items-start'>
        <Space className="flex items-start flex-col min-[1050px]:flex-row min-[1050px]:items-end">
          <div className="gap-1 flex flex-col items-start">
            <Text type="secondary" className="break-normal text-xs">
              Quantity
            </Text>
            <InputNumber
              value={item.quantity}
              onChange={changeQuantity}
              min={0}
              className="w-[100px]"
            />
          </div>

          <div className="gap-1 flex flex-col items-start">
            <Text type="secondary" className="break-normal text-xs">
              Cost Price
            </Text>
            <InputNumber
              value={item.costPrice}
              onChange={updateCostPrice}
              prefix="$"
              min={0}
              precision={2}
              className="w-[120px]"
            />
          </div>

          <div className="gap-1 flex flex-col items-start">
            <Text type="secondary" className="break-normal text-xs">
              Margin
            </Text>
            <InputNumber
              value={Number((item.margin ?? 0).toFixed(1))}
              onChange={handleMarginChange}
              min={-100}
              precision={1}
              suffix="%"
              className="w-[100px]"
            />
          </div>

          <div className="gap-1 flex flex-col items-start">
            <Text type="secondary" className="break-normal text-xs">
              Sell Price
            </Text>
            <InputNumber
              value={item.sellPrice}
              onChange={updateSellPrice}
              prefix="$"
              min={0}
              precision={2}
              className="w-[120px]"
            />
          </div>
          {!isDeliveryItem ? (
            <MaterialsButton className="hidden min-[1430px]:flex" itemId={item.id} />
          ) : (
            <DeliveryButton className="hidden min-[1430px]:flex" item={item} />
          )}
        </Space>
      </div>

      <div className="flex mt-2 min-[1430px]:mt-0 justify-between align-end">
        {!isDeliveryItem ? (
          <MaterialsButton className="flex min-[1430px]:hidden" itemId={item.id} />
        ) : (
          <DeliveryButton className="flex min-[1430px]:hidden" item={item} />
        )}
        <div className="text-right flex flex-col justify-end">
          {accessoriesCost !== 0 && (
            <Text type="secondary" className="text-xs block">
              Accessories: {formatCurrency(accessoriesCost)}
            </Text>
          )}
          {materialsCost !== 0 && (
            <Text type="secondary" className="text-xs block">
              Finishes: {formatCurrency(materialsCost)}
            </Text>
          )}
          <Text type="secondary" className="text-xs block">
            Item: {formatCurrency(itemTotal)}
          </Text>
          <Text strong className="block">
            {formatCurrency(total)}
          </Text>
        </div>
      </div>
    </div>
  )
}
